import type {
  SharedResourceShareCapability,
  SharedResourceShareFormAdapter,
  SharedResourceShareGrantRecord,
  SharedResourceShareMode,
  SharedResourceShareMutationPayload,
} from "./shared-resource-share-form-adapter";

type MessageValues = Record<string, string | number | boolean | null | undefined>;
type FormatMessageFn = (descriptor: { id: string }, values?: MessageValues) => string;

export type SharedResourceShareDrawerRecord<
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
> = {
  id: number;
  name: string;
  display_name?: string | null;
  share_mode: SharedResourceShareMode;
  share_grants: SharedResourceShareGrantRecord<TCapability, string | number>[];
};

export type SharedResourceShareDrawerAdapter<
  TRecord extends SharedResourceShareDrawerRecord<TCapability>,
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
  TPayload extends SharedResourceShareMutationPayload<
    TCapability,
    string | number
  > = SharedResourceShareMutationPayload<TCapability>,
  TResourceKey extends string | number = number,
> = SharedResourceShareFormAdapter<TRecord, TCapability, TPayload> & {
  resourceKey: string;
  i18nPrefix: string;
  persistenceKeyPrefix: string;
  getResourceKey: (record: TRecord) => TResourceKey;
  updateResource: (resourceKey: TResourceKey, payload: TPayload) => Promise<unknown>;
  getShareModeLabel: (value: SharedResourceShareMode, formatMessage?: FormatMessageFn) => string;
  supportedCapabilities: readonly TCapability[];
  identifierLabelKey?: string;
  titleLabelKey?: string;
  getIdentifierText?: (record: TRecord) => string;
  getTitleText?: (record: TRecord) => string;
};

export function createSharedResourceShareDrawerAdapter<
  TRecord extends SharedResourceShareDrawerRecord<TCapability>,
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
  TPayload extends SharedResourceShareMutationPayload<
    TCapability,
    string | number
  > = SharedResourceShareMutationPayload<TCapability>,
  TResourceKey extends string | number = number,
>(adapter: SharedResourceShareDrawerAdapter<TRecord, TCapability, TPayload, TResourceKey>) {
  return adapter;
}
